import React, { useState, useEffect } from 'react';
import { ProFormaGenericRow } from 'views/ProFormaTable/components/ProFormaGenericTable/types';
import ProFormaGenericTable from 'views/ProFormaTable/components/ProFormaGenericTable/ProFormaGenericTable';
import { ProFormaModelIds } from 'constants/proForma';
import RenderIf from 'components/RenderIf/RenderIf';
import { useProFormaSocketContext } from '../../socketContext/ProFormaSocketProvider';
import { BASE_FORECAST_COLUMNS } from './constants';
import { getDispositionPeriodSubtitle, getColumnTitle } from '../helpers';
import './ProFormaForecastTable.scss';

const ProFormaForecastTable = () => {
    const { disposition, proForma } = useProFormaSocketContext();
    const [rentalIncomeRows, setRentalIncomeRows] = useState<ProFormaGenericRow[]>([]);
    const [potentialIncomeRows, setPotentialIncomeRows] = useState<ProFormaGenericRow[]>([]);
    const [effectiveGrossRevenueRows, setEffectiveGrossRevenueRows] = useState<ProFormaGenericRow[]>([]);
    const [operatingExpensesRows, setOperatingExpensesRows] = useState<ProFormaGenericRow[]>([]);
    const [capitalExpendituresRows, setCapitalExpendituresRows] = useState<ProFormaGenericRow[]>([]);

    const isApartment = [ProFormaModelIds.APARTMENT, ProFormaModelIds.APARTMENT_WITH_RETAIL].includes(proForma?.proFormaModelId);

    useEffect(() => {
        if (isApartment) {
            const rentalIncome: ProFormaGenericRow[] = [
                {
                    id: 'gross-rent',
                    name: 'Gross rent',
                    untrended: disposition?.grossRent?.untrended?.toString() || '-',
                    trended: disposition?.grossRent?.trended?.toString() || '-',
                    sale: disposition?.grossRent?.sale?.toString() || '-',
                },
                {
                    id: 'gross-rent-adjustments',
                    name: 'Gross rent adjustments',
                    untrended: disposition?.grossRentAdjustments?.untrended?.toString() || '-',
                    trended: disposition?.grossRentAdjustments?.trended?.toString() || '-',
                    sale: disposition?.grossRentAdjustments?.sale?.toString() || '-',
                    isNegative: true,
                },
            ];
            setRentalIncomeRows(rentalIncome);
        } else {
            // Non-apartment types - different structure
            const rentalIncome: ProFormaGenericRow[] = [
                {
                    id: 'total-rental-income',
                    name: 'Total rental income',
                    untrended: disposition?.totalRentalIncome?.untrended?.toString() || '-',
                    trended: disposition?.totalRentalIncome?.trended?.toString() || '-',
                    sale: disposition?.totalRentalIncome?.sale?.toString() || '-',
                },
                {
                    id: 'total-recovery-income',
                    name: 'Total recovery income',
                    untrended: disposition?.totalRecoveryIncome?.untrended?.toString() || '-',
                    trended: disposition?.totalRecoveryIncome?.trended?.toString() || '-',
                    sale: disposition?.totalRecoveryIncome?.sale?.toString() || '-',
                },
                {
                    id: 'total-other-income',
                    name: 'Total other income',
                    untrended: disposition?.totalOtherIncome?.untrended?.toString() || '-',
                    trended: disposition?.totalOtherIncome?.trended?.toString() || '-',
                    sale: disposition?.totalOtherIncome?.sale?.toString() || '-',
                },
            ];
            setRentalIncomeRows(rentalIncome);
        }

        setPotentialIncomeRows([
            {
                id: 'total-other-income',
                name: 'Total other income',
                untrended: disposition?.totalOtherIncome?.untrended?.toString() || '-',
                trended: disposition?.totalOtherIncome?.trended?.toString() || '-',
                sale: disposition?.totalOtherIncome?.sale?.toString() || '-',
            },
        ]);

        const effectiveGrossRevenue: ProFormaGenericRow[] = [
            {
                id: 'income-adjustments',
                name: 'Income adjustments',
                untrended: disposition?.totalIncomeAdjustments?.untrended?.toString() || '-',
                trended: disposition?.totalIncomeAdjustments?.trended?.toString() || '-',
                sale: disposition?.totalIncomeAdjustments?.sale?.toString() || '-',
                isNegative: true,
            },
        ];
        setEffectiveGrossRevenueRows(effectiveGrossRevenue);

        // Operating Expenses section
        const operatingExpenses: ProFormaGenericRow[] = [
            {
                id: 'total-operating-expenses',
                name: 'Total operating expenses',
                untrended: disposition?.totalOperatingExpenses?.untrended?.toString() || '-',
                trended: disposition?.totalOperatingExpenses?.trended?.toString() || '-',
                sale: disposition?.totalOperatingExpenses?.sale?.toString() || '-',
                isNegative: true,
            },
        ];
        setOperatingExpensesRows(operatingExpenses);

        // Capital Expenditures section
        const capitalExpenditures: ProFormaGenericRow[] = [
            {
                id: 'total-capital-expenditures',
                name: 'Total capital expenditures',
                untrended: disposition?.totalCapitalExpenditures?.untrended?.toString() || '-',
                trended: disposition?.totalCapitalExpenditures?.trended?.toString() || '-',
                sale: disposition?.totalCapitalExpenditures?.sale?.toString() || '-',
                isNegative: true,
            },
        ];
        setCapitalExpendituresRows(capitalExpenditures);
    }, [disposition, proForma]);

    const forecastColumns = BASE_FORECAST_COLUMNS.map(col => {
        if (['untrended', 'trended', 'sale'].includes(col.key)) {
            return {
                ...col,
                header: (
                    <div className="ProFormaForecastTable__header">
                        <div>{getColumnTitle(col.key)}</div>
                        <small className="text-muted">{getDispositionPeriodSubtitle(col.key, disposition)}</small>
                    </div>
                ),
            };
        }
        return col;
    });

    return (
        <div className="d-flex flex-column p-3">
            <ProFormaGenericTable
                columns={forecastColumns}
                rows={rentalIncomeRows}
                setRows={setRentalIncomeRows}
                hideAddButton
                totalText={isApartment ? 'Total rental income' : 'Total potential income'}
                totalValues={{
                    untrended: isApartment ? disposition?.totalRentalIncome?.untrended?.toString() || '—'
                        : disposition?.totalPotentialIncome?.untrended?.toString() || '-',
                    trended: isApartment ? disposition?.totalRentalIncome?.trended?.toString() || '—'
                        : disposition?.totalPotentialIncome?.trended?.toString() || '—',
                    sale: isApartment ? disposition?.totalRentalIncome?.sale?.toString() || '—'
                        : disposition?.totalPotentialIncome?.sale?.toString() || '—',
                }}
                hideCashFlowTotals
                fixedColumnsMaxWidth="100%"
            />

            <RenderIf isTrue={isApartment}>
                <ProFormaGenericTable
                    columns={forecastColumns}
                    rows={potentialIncomeRows}
                    setRows={setPotentialIncomeRows}
                    hideAddButton
                    hideHeader
                    totalText="Total potential income"
                    totalValues={{
                        untrended: disposition?.totalPotentialIncome?.untrended?.toString() || '-',
                        trended: disposition?.totalPotentialIncome?.trended?.toString() || '-',
                        sale: disposition?.totalPotentialIncome?.sale?.toString() || '-',
                    }}
                    hideCashFlowTotals
                    fixedColumnsMaxWidth="100%"
                />
            </RenderIf>
            <ProFormaGenericTable
                columns={forecastColumns}
                rows={effectiveGrossRevenueRows}
                setRows={setEffectiveGrossRevenueRows}
                hideAddButton
                hideHeader
                totalText="Effective gross revenue"
                totalValues={{
                    untrended: disposition?.totalEffectiveGrossRevenue?.untrended?.toString() || '-',
                    trended: disposition?.totalEffectiveGrossRevenue?.trended?.toString() || '-',
                    sale: disposition?.totalEffectiveGrossRevenue?.sale?.toString() || '-',
                }}
                hideCashFlowTotals
                fixedColumnsMaxWidth="100%"
            />
            <ProFormaGenericTable
                hideHeader
                columns={forecastColumns}
                rows={operatingExpensesRows}
                setRows={setOperatingExpensesRows}
                hideAddButton
                totalText="Net operating income"
                totalValues={{
                    untrended: disposition?.netOperatingIncome?.untrended?.toString() || '-',
                    trended: disposition?.netOperatingIncome?.trended?.toString() || '-',
                    sale: disposition?.netOperatingIncome?.sale?.toString() || '-',
                }}
                hideCashFlowTotals
                fixedColumnsMaxWidth="100%"
            />
            <ProFormaGenericTable
                columns={forecastColumns}
                rows={capitalExpendituresRows}
                setRows={setCapitalExpendituresRows}
                hideAddButton
                hideHeader
                totalText="Cash flow from operations"
                totalValues={{
                    untrended: disposition?.cashFlowFromOperations?.untrended?.toString() || '-',
                    trended: disposition?.cashFlowFromOperations?.trended?.toString() || '-',
                    sale: disposition?.cashFlowFromOperations?.sale?.toString() || '-',
                }}
                hideCashFlowTotals
                fixedColumnsMaxWidth="100%"
            />
        </div>
    );
};

export default ProFormaForecastTable;
